import React, { useState, useEffect } from "react";
import { Form, Button, Container, Alert, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { useAuth } from "../../Context/AuthContext";
import { useEditMode } from "../../Context/EditModeContext";
import * as pdfjsLib from "pdfjs-dist";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const PdfLesson = ({ lessonId, setLesson, lesson }) => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const { id } = useParams();
  const [successMessage, setSuccessMessage] = useState("");
  const { authData } = useAuth();
  const { isEditMode } = useEditMode();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/programs/${id}`
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || t("error_occurred"));
        }

        setProgram(data.program);
      } catch (err) {
        setError(err.message);
        console.error(err);
      }
    };

    fetchProgram();
  }, [id, t]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    setError("");
    setSuccessMessage("");
    e.preventDefault();
    if (file) {
      const formData = new FormData();
      formData.append("pdf", file);

      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/lessons/lesson/${lesson._id}/upload-pdf`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || t("error_occurred"));
        }

        setSuccessMessage(data.message);
        setLesson(data.lesson);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        console.error(err);
        setIsLoading(false);
      }
    }
  };

  if (!program) {
    return <div>{t("loading_program")}</div>;
  }

  if (!lesson) {
    return <div>{t("loading_lesson")}</div>;
  }

  if (lesson.content !== "Non Defini") {
    return (
      <>
        {isEditMode && program.created_by._id === authData.userId ? (
          <Container className="p-5 text-center mt-3">
            <h2 className="mb-4">{t("edit_pdf")}</h2>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formFile" className="mb-3">
                <Form.Label className="fw-bold">
                  {t("choose_or_drop_file")}
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
              </Form.Group>
              <Button variant="primary" type="submit">
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  t("add_pdf_file")
                )}
              </Button>
            </Form>
            {file && (
              <Alert variant="info" className="mt-3">
                {t("selected_file")}: {file.name}
              </Alert>
            )}
            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}
            {successMessage !== "" && (
              <Alert variant="success" className="mt-3">
                {successMessage}
              </Alert>
            )}
          </Container>
        ) : (
          <Container className="text-center mb-5">
            <h2 className="mb-4">{t("pdf_lesson")}</h2>
            <Worker
              workerUrl={`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`}
            >
              <div
                style={{
                  height: "750px",
                  border: "1px solid rgba(0, 0, 0, 0.3)",
                }}
              >
                <Viewer
                  fileUrl={lesson.content}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </div>
            </Worker>
            <div style={{ height: "100px" }}></div> {/* Espace en bas */}
          </Container>
        )}
      </>
    );
  }

  if (isEditMode && program.created_by._id === authData.userId) {
    return (
      <Container className="p-5 text-center mt-3">
        <h2 className="mb-4">{t("edit_pdf")}</h2>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="formFile" className="mb-3">
            <Form.Label className="fw-bold">
              {t("choose_or_drop_file")}
            </Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
            />
          </Form.Group>
          <Button variant="primary" type="submit">
            {isLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              t("add_pdf_file")
            )}
          </Button>
        </Form>
        {file && (
          <Alert variant="info" className="mt-3">
            {t("selected_file")}: {file.name}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
        {successMessage !== "" && (
          <Alert variant="success" className="mt-3">
            {successMessage}
          </Alert>
        )}
      </Container>
    );
  }

  if (program.created_by._id !== authData.userId) {
    return <div className="text-center">{t("lesson_empty_message")}</div>;
  }

  return (
    <Container className="p-5 border rounded bg-light text-center">
      <p>{t("lesson_empty_message")}</p>
    </Container>
  );
};

export default PdfLesson;
